import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { LogOut, Users, Settings, PiggyBank, TrendingUp, RefreshCw, ArrowDownCircle, ArrowUpCircle, Bell } from 'lucide-react';

const API = '/api';

export default function AdminDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ partners: 0, totalCapital: 0, totalProfits: 0 });
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [pRes, rRes] = await Promise.all([
        axios.get(`${API}/partners/with-credentials`, { headers }),
        axios.get(`${API}/admin/all-requests`, { headers }).catch(() => ({ data: { deposits: [], withdrawals: [] } })),
      ]);
      const partners = pRes.data || [];
      const totalCapital = partners.reduce((s, p) => s + (p.initial_capital || 0), 0);
      const totalProfits = partners.reduce((s, p) => s + (p.total_profits || 0), 0);
      setStats({ partners: partners.length, totalCapital, totalProfits });

      const deps = rRes.data?.deposits?.filter(r => r.status === 'pending')?.length || 0;
      const withs = rRes.data?.withdrawals?.filter(r => r.status === 'pending')?.length || 0;
      setPendingCount(deps + withs);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const menuItems = [
    {
      title: 'الشركاء والأرباح',
      description: 'إدارة الشركاء وتوزيع الأرباح',
      icon: Users,
      color: 'from-emerald-500 to-emerald-600',
      path: '/partners',
      badge: null
    },
    {
      title: 'الإعدادات',
      description: 'أرقام الدفع وحماية البيانات',
      icon: Settings,
      color: 'from-slate-500 to-slate-600',
      path: '/admin/settings',
      badge: null
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30 flex items-center justify-center" dir="rtl">
        <RefreshCw className="h-12 w-12 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30" dir="rtl" data-testid="admin-dashboard">
      <header className="bg-white/90 backdrop-blur-md border-b border-emerald-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-black text-emerald-700">افيوس جروب</h1>
              <p className="text-sm text-slate-500">لوحة تحكم المدير</p>
            </div>
            <div className="flex items-center gap-3">
              {pendingCount > 0 && (
                <Button variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50 relative"
                  onClick={() => navigate('/partners')} data-testid="pending-requests-btn">
                  <Bell className="h-4 w-4 ml-1" />
                  {pendingCount} طلب معلق
                </Button>
              )}
              <Button onClick={onLogout} variant="outline" className="rounded-full border-slate-300" data-testid="logout-button">
                <LogOut className="ml-2 h-4 w-4" />
                خروج
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="overflow-hidden border-0 shadow-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
            <CardContent className="p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm font-medium mb-2">عدد الشركاء</p>
                  <p className="text-4xl font-black" data-testid="partners-count">{stats.partners}</p>
                </div>
                <Users className="h-14 w-14 text-emerald-200/40" />
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden border-0 shadow-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardContent className="p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium mb-2">إجمالي رؤوس الأموال</p>
                  <p className="text-4xl font-black" data-testid="total-capital">{stats.totalCapital.toLocaleString()}</p>
                  <p className="text-blue-200 text-sm mt-1">ج.م</p>
                </div>
                <PiggyBank className="h-14 w-14 text-blue-200/40" />
              </div>
            </CardContent>
          </Card>
          <Card className="overflow-hidden border-0 shadow-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white">
            <CardContent className="p-8">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm font-medium mb-2">إجمالي الأرباح</p>
                  <p className="text-4xl font-black" data-testid="total-profits">{stats.totalProfits.toLocaleString()}</p>
                  <p className="text-amber-200 text-sm mt-1">ج.م</p>
                </div>
                <TrendingUp className="h-14 w-14 text-amber-200/40" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Menu */}
        <div className="grid grid-cols-2 gap-6">
          {menuItems.map((item) => (
            <Card key={item.title}
              className="group cursor-pointer border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
              onClick={() => navigate(item.path)} data-testid={`menu-${item.title}`}>
              <CardContent className="p-0">
                <div className={`p-8 bg-gradient-to-br ${item.color} flex items-center justify-center relative`}>
                  <item.icon className="h-16 w-16 text-white" />
                </div>
                <div className="p-6 bg-white">
                  <h3 className="text-xl font-bold text-slate-800 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-500">{item.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      <footer className="mt-auto py-6 text-center text-sm text-slate-400">
        <p>افيوس جروب &copy; {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}
